import {
  AvSpeichernSessiondatenAntrag,
  BankverbindungAntrag,
  BeiblattDruckdaten,
  DokumentenErstellungRequestModel,
  EinreichenRequestModel,
  EuBPDruckdaten,
  FomWithBankNameAntrag,
  InfopaketDruckdaten,
  KommunikationsArt,
  PersonAntrag,
  PKBDruckdaten,
  Role,
  SparteAntrag,
  SparteDruckdaten,
  UebergabeVertragsschlussverfahren,
  Vertragsschlussverfahren,
  Vertragsschlussverfahren as BffVertragsschlussverfahren,
  Zahlungsart
} from '@ps20/bff-models';
import { convertSafeDomaenenwert, getTarifbereich, removeBlanksFromIban } from '@ps20-frontend/frontend-models';
import { IAppState, selectActiveVermittlerNummer } from '@ps20-frontend/apps/checkout-store';
import { DatenhaltungKDV, isMaklerbetreuerOrMaklerOrUvm } from '@ps20-frontend/shop-integration-redux';
import { convertDate } from '@ps20-frontend/utils';
import { Zahlung } from '../../reducer/zahlung.reducer';
import { Vermittler } from '../../reducer/vermittler.reducer';
import { TransientState } from '../../reducer/transient-state.reducer';
import { mapBeiblatt } from '../druck/beiblatt.mapper';
import { mapEUBP, mapInfopaket } from '../druck/infopaket.mapper';
import { mapPKB, mapVermittlerWechselDaten } from '../druck/pkb.mapper';
import { getProgrammteilnahme } from '../../model/policeVersandArt';
import { createSparten } from './spartenDatenMapper';

export function generateEinreichenRequestModel(
  kdvDaten: AvSpeichernSessiondatenAntrag,
  state: IAppState,
  vorschlagsdaten: Array<SparteAntrag>,
): EinreichenRequestModel {
  const activeVermittlerNummer = selectActiveVermittlerNummer(state.vermittler);
  return {
    kdvDaten,
    versicherungsnehmer: {
      personenID: state.versicherungsnehmer.kundenverwaltungID,
      kundennummer: state.versicherungsnehmer.kundennummer,
      anrede: state.versicherungsnehmer.anrede,
      nachname: state.versicherungsnehmer.nachname,
      vorname: state.versicherungsnehmer.vorname,
      firmenname: state.versicherungsnehmer.firmenname,
      juristisch: state.versicherungsnehmer.juristisch,
      businessForm: state.versicherungsnehmer.businessForm,
      namenszusatz: state.versicherungsnehmer.namenszusatz,
      akadgrad: state.versicherungsnehmer.akadgrad,
      anrtitel: state.versicherungsnehmer.anrtitel,
      famstand: state.versicherungsnehmer.famstand,
      gebname: state.versicherungsnehmer.gebname,
      heimatort: state.versicherungsnehmer.heimatort,
      adelsbez: state.versicherungsnehmer.adelsbez,
      adelstitel: state.versicherungsnehmer.adelstitel,
      geburtsland: state.versicherungsnehmer.geburtsland,
      geburtsdatum: convertDate(state.versicherungsnehmer.geburtsdatum),
      staatsbschaft: state.versicherungsnehmer.staatsbschaft,
      adresse: {
        plz: state.versicherungsnehmer.plz,
        ort: state.versicherungsnehmer.ort,
        ortsteil: state.versicherungsnehmer.ortsteil,
        strasse: state.versicherungsnehmer.strasse,
        hausnummer: state.versicherungsnehmer.hausnummer,
        hausnrzus: state.versicherungsnehmer.hausnrzus,
        land: state.versicherungsnehmer.land,
        adresszusatz: state.versicherungsnehmer.adresszusatz,
        adresszusatzkennzeichen:
          state.versicherungsnehmer.adresszusatzkennzeichen &&
          state.versicherungsnehmer.adresszusatzkennzeichen.wert,
        invalidAddress: state.versicherungsnehmer.addressInvalid,
      },
      kommunikationsdaten: [
        {
          kommunikationsArt: KommunikationsArt.EMAIL,
          wert: state.versicherungsnehmer.email.wert,
          verwendung: state.versicherungsnehmer.email.verwendung,
          herkunft: state.versicherungsnehmer.email.herkunft,
          vorbelegt: state.versicherungsnehmer.email.vorbelegt,
          changedByProgrammTeilnahme: state.versicherungsnehmer.email.changedByProgrammteilnahme,
        },
        {
          kommunikationsArt: KommunikationsArt.HANDY,
          wert: adjustTelefonnummer(
            state.versicherungsnehmer.mobil.wert,
            state.versicherungsnehmer.mobil.laendervorwahl,
          ),
          capWert: state.versicherungsnehmer.mobil.wert,
          verwendung: state.versicherungsnehmer.mobil.verwendung,
          herkunft: state.versicherungsnehmer.mobil.herkunft,
          vorbelegt: state.versicherungsnehmer.mobil.vorbelegt,
        },
        {
          kommunikationsArt: KommunikationsArt.TELEFON,
          wert: adjustTelefonnummer(
            state.versicherungsnehmer.telefon.wert,
            state.versicherungsnehmer.telefon.laendervorwahl,
          ),
          verwendung: state.versicherungsnehmer.telefon.verwendung,
          herkunft: state.versicherungsnehmer.telefon.herkunft,
          vorbelegt: state.versicherungsnehmer.telefon.vorbelegt,
        },
      ],
    },
    kontoinhaber: getKontoinhaber(state),
    vermittler: {
      vorname: state.vermittler.vorname,
      nachname: state.vermittler.nachname,
      vermittlernummern: [
        {
          zweigniederlassung: activeVermittlerNummer.zweigniederlassung,
          geschaeftsstelle: activeVermittlerNummer.geschaeftsstelle,
          vertreternummer: activeVermittlerNummer.vertreternummer,
        },
      ],
      telefonnummer: state.vermittler.telefon,
      kommunikationsdaten: null,
      bnrbs: state.vermittler.bnrb.length > 0 ? [state.vermittler.bnrb] : null,
      fomWithBankNameAntrag: getFremdordnungsmerkmal(state.vermittler),
      isInkasso: state.vermittler.isInkasso,
    },
    vertrag: {
      tarifbereich: getTarifbereich(state.versicherungsnehmer),
      unfallZaehlstuecke: state.unfallZaehlstuecke,
      effectiveZaehlstuecke: state.effectiveZaehlstuecke,
      uebergabeVertragsschlussverfahren: UebergabeVertragsschlussverfahren.DIGITAL,
      vertragsschlussverfahren: BffVertragsschlussverfahren[state.vertragsschlussverfahren],
      zahlungsmethode: {
        zahlweise: state.zahlung.zahlweise,
        zahlungsart: state.zahlung.zahlungsart,
      },
    },
    sammelantragsnummer: state.sammelantragsnummer,
    fernabsatz: state.fernabsatz ?? undefined,
    mindestlaufzeitSelected: state.mindestvertragslaufzeit ?? undefined,
    sparten: createSparten(vorschlagsdaten, state),
    programmteilnahmen: {
      neueProgrammteilnahme: getProgrammteilnahme(state.policeVersand.versandArt),
      vorhandeneProgrammteilnahme: state.versicherungsnehmer.email.programmName,
    },
    dokumente: null,
  };
}

// Add '0' between laendervowahl and wert if there is none
export function adjustTelefonnummer(wert: string, laendervorwahl?: string): string {
  if (!wert || !laendervorwahl) {
    return wert;
  }
  const wertOhneLaendervorwahl = wert.replace(`+${laendervorwahl}`, '').replace(/\D/g, '');
  return wertOhneLaendervorwahl.startsWith('0')
    ? wert
    : `+${laendervorwahl}0${wertOhneLaendervorwahl}`;
}

export function getFremdordnungsmerkmal(vermittler: Vermittler): FomWithBankNameAntrag {
  const acitveVtnr = selectActiveVermittlerNummer(vermittler);
  const fomWithBankNameAntrag: FomWithBankNameAntrag = {
    vermittlerFom: acitveVtnr.vermittlerFom,
  };
  if (vermittler.fom) {
    fomWithBankNameAntrag.fremdordnungsmerkmal = vermittler.fom;
  }
  return fomWithBankNameAntrag;
}

export function getKontoinhaber(state: IAppState): PersonAntrag {
  if ([Zahlungsart.RECHNUNG, Zahlungsart.MAKLER_INKASSO].includes(state.zahlung.zahlungsart)) {
    return null;
  } else {
    const kontoinhaber = state.zahlung.kontoinhaber;
    return {
      personenID: kontoinhaber.kundenverwaltungID,
      kundennummer: kontoinhaber.kundennummer,
      anrede: kontoinhaber.anrede,
      nachname: kontoinhaber.nachname,
      vorname: kontoinhaber.vorname,
      firmenname: kontoinhaber.firmenname,
      juristisch: kontoinhaber.juristisch,
      businessForm: kontoinhaber.businessForm,
      namenszusatz: convertSafeDomaenenwert(kontoinhaber.namenszusatz),
      akadgrad: convertSafeDomaenenwert(kontoinhaber.akadgrad),
      famstand: convertSafeDomaenenwert(kontoinhaber.famstand),
      heimatort: kontoinhaber.heimatort,
      adelsbez: convertSafeDomaenenwert(kontoinhaber.adelsbez),
      adelstitel: convertSafeDomaenenwert(kontoinhaber.adelstitel),
      geburtsland: convertSafeDomaenenwert(kontoinhaber.geburtsland),
      geburtsdatum: kontoinhaber.geburtsdatum ? convertDate(kontoinhaber.geburtsdatum) : null,
      staatsbschaft: convertSafeDomaenenwert(kontoinhaber.staatsbschaft),
      bankverbindung: generateBankverbindungAntrag(state.zahlung),
      adresse: {
        plz: kontoinhaber.adresse.plz,
        ort: kontoinhaber.adresse.ort,
        strasse: kontoinhaber.adresse.strasse,
        hausnummer: kontoinhaber.adresse.hausnummer,
        land: convertSafeDomaenenwert(kontoinhaber.adresse.land),
      },
    };
  }
}

function generateBankverbindungAntrag(zahlung: Zahlung): BankverbindungAntrag {
  const bankverbindung: BankverbindungAntrag = {
    iban: removeBlanksFromIban(zahlung.iban),
    verwendungszweck: zahlung.verwendungszweck,
  };

  if (zahlung.bankname) {
    bankverbindung.bankname = zahlung.bankname;
  }

  if (zahlung.bic) {
    bankverbindung.bic = zahlung.bic;
  }

  return bankverbindung;
}

export function createKdvDaten(state: IAppState): AvSpeichernSessiondatenAntrag | undefined {
  if (state.kdv) {
    return {
      vorgangid: getVorgangsIdByRole(state.transientState, state.kdv),
      perid: state.kdv.perId,
      notiz: state.kdv.vermerk,
      gesamtbeitrag: state.kdv.gesamtbeitrag,
      zahlweise: state.zahlung.zahlweise,
      azamCryptId: state.kdv.azamCryptId,
    };
  }
}

function getVorgangsIdByRole(transientState: TransientState, kdv: DatenhaltungKDV): string {
  return ({
    [Role.MAKLERBETREUER]: kdv.azamVorgangsId,
    [Role.MAKLER]: kdv.azamVorgangsId,
    [Role.UVM]: kdv.azamVorgangsId,
    [Role.NEBENBERUFSVERTRETER]: kdv.azamVorgangsId,
    [Role.OLB]: kdv.azamVorgangsId,
    [Role.GENO]: kdv.azamVorgangsId,
    [Role.UWPSHRK]: kdv.azamVorgangsId,
    [Role.UNDERWRITER_UNFALL]: kdv.azamVorgangsId,
    [Role.AGENTURANGESTELLTER]: kdv.azamVorgangsId,
    [Role.LEITENDER_KUNDENBETREUER]: kdv.azamVorgangsId,
    [Role.VTTEL]: kdv.azamVorgangsId,
    [Role.VTGEV]: kdv.azamVorgangsId,
    [Role.VTKUS]: kdv.azamVorgangsId,
    [Role.VTKVM]: kdv.azamVorgangsId,
    [Role.VERTRETER]: kdv.vorId,
  } as Partial<Record<Role, string>>)[transientState.role];
}

export function createDokumente(
  state: IAppState,
  spartenDruckdaten: Array<SparteDruckdaten>,
): DokumentenErstellungRequestModel<InfopaketDruckdaten | BeiblattDruckdaten>[] {
  const beiblattRequestModel: Array<DokumentenErstellungRequestModel<BeiblattDruckdaten>> = [
    mapBeiblatt(state, spartenDruckdaten),
  ];

  const spartenDruckdatenWithoutBeibehaltung: Array<SparteDruckdaten> = spartenDruckdaten.filter(
    druckdaten => druckdaten.beibehalten !== true,
  );

  const infopakete: Array<DokumentenErstellungRequestModel<InfopaketDruckdaten>> =
    spartenDruckdatenWithoutBeibehaltung.map(sparteDruckdaten =>
      mapInfopaket(state, sparteDruckdaten),
    );
  const pokobaModel: Array<DokumentenErstellungRequestModel<PKBDruckdaten>> = spartenDruckdaten.map(
    sparteDruckdaten => {
      return mapPKB(state, sparteDruckdaten, mapVermittlerWechselDaten(spartenDruckdaten));
    },
  );

  const eupb: Array<DokumentenErstellungRequestModel<EuBPDruckdaten>> = isMaklerbetreuerOrMaklerOrUvm(
    state.transientState.role,
  ) || state.vertragsschlussverfahren === Vertragsschlussverfahren.KUNDENANTRAG
    ? []
    : spartenDruckdatenWithoutBeibehaltung.map(sparteDruckdaten =>
        mapEUBP(state, sparteDruckdaten),
      );

  return [...beiblattRequestModel, ...pokobaModel, ...infopakete, ...eupb];
}
